import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../features/theme/context/ThemeContext';
import midusStatusBlack from '../assets/MIDUS_STATUS_BLACK.svg';
import midusStatusWhite from '../assets/MIDUS_STATUS_WHITE.svg';
import NvgtrShimmerLoader from '../features/chat/components/NvgtrShimmerLoader';

const StatusPage = () => {
  const { darkMode } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentStatus, setCurrentStatus] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [uptime, setUptime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const [activeTab, setActiveTab] = useState('status'); // 'status' or 'history'
  const [expandedServices, setExpandedServices] = useState({}); // Track which services are expanded
  
  // Subscription modal state
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionEmail, setSubscriptionEmail] = useState('');
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState('');
  const [showSubscriptionConfirmedBanner, setShowSubscriptionConfirmedBanner] = useState(false);
  const [subscriptionServices, setSubscriptionServices] = useState(['apis', 'midus', 'compliance']);
  const [statusDaysCount, setStatusDaysCount] = useState(90); // 60 on mobile, 90 on desktop
  /** ISO date from API — bars span from this day through today (capped at statusDaysCount). */
  const [trackingSince, setTrackingSince] = useState(null);
  /** ISO date — Midus for Compliance bars before this day are "not yet available" (not operational). */
  const [complianceTrackingSince, setComplianceTrackingSince] = useState(null);

  const complianceLaunchStart = useMemo(() => {
    if (!complianceTrackingSince) return null;
    const parsed = new Date(complianceTrackingSince);
    if (Number.isNaN(parsed.getTime())) return null;
    const start = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
    const today = new Date();
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    if (start > todayStart) return null;
    return start;
  }, [complianceTrackingSince]);

  const isComplianceStatusService = (serviceName) => {
    const raw = String(serviceName || '').toLowerCase().replace(/\s+/g, '');
    if (raw === 'midusforcompliance') return true;
    return ['compliance', 'compliance_login', 'parsing', 'downloads', 'rulebooks'].includes(raw);
  };

  const dayIsBeforeComplianceLaunch = (date) => {
    if (!complianceLaunchStart) return false;
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    return d < complianceLaunchStart;
  };

  // Responsive: show 60 days on mobile, 90 on desktop (matchMedia aligns with Tailwind md: 768px)
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const update = () => setStatusDaysCount(mq.matches ? 90 : 60);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  /** Calendar dates for each uptime bar (oldest → newest), respecting trackingSince and max width. */
  const getBarDates = useCallback(() => {
    const maxSpan = statusDaysCount;
    const today = new Date();
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    let trackingStart = null;
    if (trackingSince) {
      const parsed = new Date(trackingSince);
      if (!Number.isNaN(parsed.getTime())) {
        trackingStart = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
        if (trackingStart > todayStart) trackingStart = null;
      }
    }

    const countInclusiveDays = (start, end) => {
      let n = 0;
      const d = new Date(start);
      while (d <= end) {
        n += 1;
        d.setDate(d.getDate() + 1);
      }
      return n;
    };

    let startDate;
    let count;
    if (trackingStart) {
      const natural = countInclusiveDays(trackingStart, todayStart);
      if (natural <= maxSpan) {
        startDate = new Date(trackingStart);
        count = natural;
      } else {
        startDate = new Date(todayStart);
        startDate.setDate(startDate.getDate() - (maxSpan - 1));
        count = maxSpan;
      }
    } else {
      startDate = new Date(todayStart);
      startDate.setDate(startDate.getDate() - (maxSpan - 1));
      count = maxSpan;
    }

    const dates = [];
    const cur = new Date(startDate);
    for (let i = 0; i < count; i++) {
      dates.push(new Date(cur));
      cur.setDate(cur.getDate() + 1);
    }
    return dates;
  }, [statusDaysCount, trackingSince]);

  /** True if a resolved/active incident overlaps a calendar day (uses API startedAt / resolvedAt). */
  const incidentAffectsCalendarDay = (inc, dayDate) => {
    const dayStart = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
    const dayEnd = new Date(dayStart);
    dayEnd.setDate(dayEnd.getDate() + 1);
    const start = new Date(inc.startedAt || inc.activatedAt || 0).getTime();
    const end = inc.resolvedAt ? new Date(inc.resolvedAt).getTime() : Date.now();
    if (Number.isNaN(start)) return false;
    return start < dayEnd.getTime() && end >= dayStart.getTime();
  };

  // Helper function to ensure consistent API URL format
  const getApiUrl = useCallback((endpoint) => {
    // Get the base API URL 
    const apiUrl = process.env.REACT_APP_API_URL || 
      (process.env.NODE_ENV === 'development' ? 'http://192.168.86.56:8081' : 'https://api.midus.co');
    
    // Clean the URL and normalize format
    let baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
    
    // Check if the baseUrl already includes '/api' and handle endpoint appropriately
    if (baseUrl.endsWith('/api')) {
      // Remove '/api' from the beginning of the endpoint if present
      const cleanEndpoint = endpoint.startsWith('api/') ? endpoint.substring(4) : endpoint;
      return `${baseUrl}/${cleanEndpoint}`;
    }
    
    // If endpoint doesn't start with 'api/', add it
    const cleanEndpoint = endpoint.startsWith('api/') ? endpoint : `api/${endpoint}`;
    return `${baseUrl}/${cleanEndpoint}`;
  }, []);

  // Update favicon based on status
  const updateFavicon = useCallback((status) => {
    console.log('StatusPage: Updating favicon with status:', status);
    
    // Try immediate update
    if (window.updateStatusFavicon) {
      console.log('StatusPage: Calling updateStatusFavicon directly');
      window.updateStatusFavicon(status);
    } else {
      console.log('StatusPage: updateStatusFavicon not available, dispatching event');
      // Fallback: dispatch event that the script can listen for
      window.dispatchEvent(new CustomEvent('statusUpdate', { 
        detail: status 
      }));
    }
  }, []);

  // Fetch current status
  const fetchCurrentStatus = useCallback(async () => {
    try {
      const response = await fetch(getApiUrl('status/current'));
      const data = await response.json();
      if (data.success) {
        if (data.statusPage?.trackingSince) {
          setTrackingSince(data.statusPage.trackingSince);
        } else {
          setTrackingSince(null);
        }
        setComplianceTrackingSince(data.statusPage?.complianceTrackingSince || null);
        setCurrentStatus(prevStatus => {
          // Only update if the status actually changed to prevent unnecessary re-renders
          const newStatus = data.status;
          if (JSON.stringify(prevStatus) !== JSON.stringify(newStatus)) {
            console.log('StatusPage: Status changed, updating favicon');
            // Update favicon when status changes
            updateFavicon(newStatus);
            return newStatus;
          }
          return prevStatus;
        });
      }
    } catch (err) {
      console.error('Error fetching current status:', err);
      // Fallback to operational status
      const fallbackStatus = {
        isActive: false,
        title: 'All Systems Operational',
        message: 'All systems are running normally.',
        affectedServices: []
      };
      setCurrentStatus(fallbackStatus);
      setComplianceTrackingSince(null);
      updateFavicon(fallbackStatus);
    }
  }, [getApiUrl, updateFavicon]);

  // Fetch incident history
  const fetchIncidents = useCallback(async () => {
    try {
      const response = await fetch(getApiUrl('status/history?limit=500'));
      const data = await response.json();
      if (data.success) {
        setIncidents(data.incidents);
      }
    } catch (err) {
      console.error('Error fetching incidents:', err);
      setIncidents([]);
    }
  }, [getApiUrl]);

  // Fetch uptime statistics
  const fetchUptime = useCallback(async () => {
    try {
      const response = await fetch(getApiUrl('status/uptime'));
      const data = await response.json();
      if (data.success) {
        setUptime(data.uptime);
      }
    } catch (err) {
      console.error('Error fetching uptime:', err);
      setUptime({
        last30Days: { percentage: 99.9, incidents: 0 },
        last90Days: { percentage: 99.9, incidents: 0 },
        services: {
          apis: { percentage: 99.9, downtime: 0 },
          'heymidus.com': { percentage: 99.9, downtime: 0 },
          login: { percentage: 99.9, downtime: 0 },
          signup: { percentage: 99.9, downtime: 0 },
          chat: { percentage: 99.9, downtime: 0 },
          search: { percentage: 99.9, downtime: 0 },
          deep_research: { percentage: 99.9, downtime: 0 },
          image_gen: { percentage: 99.9, downtime: 0 },
          connectors: { percentage: 99.9, downtime: 0 },
          memory: { percentage: 99.9, downtime: 0 },
          superbucket: { percentage: 99.9, downtime: 0 },
          projects: { percentage: 99.9, downtime: 0 },
          compliance: { percentage: 100, downtime: 0 },
          compliance_login: { percentage: 100, downtime: 0 },
          parsing: { percentage: 100, downtime: 0 },
          downloads: { percentage: 100, downtime: 0 },
          rulebooks: { percentage: 100, downtime: 0 }
        }
      });
    }
  }, [getApiUrl]);

  // Handle subscription
  const handleSubscription = async (e) => {
    e.preventDefault();
    
    if (!subscriptionEmail || !subscriptionEmail.includes('@')) {
      setSubscriptionMessage('Please enter a valid email address');
      return;
    }

    if (subscriptionServices.length === 0) {
      setSubscriptionMessage('Please select at least one service');
      return;
    }
    
    setSubscriptionLoading(true);
    setSubscriptionMessage('');
    
    try {
      const response = await fetch(getApiUrl('status/subscribe'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: subscriptionEmail, services: subscriptionServices }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Check if the message indicates already subscribed
        if (data.message && data.message.includes('already subscribed')) {
          setSubscriptionMessage('You are already subscribed to status updates.');
        } else if (data.message && data.message.includes('check your email')) {
          setSubscriptionMessage('Check your email and click the confirmation link to complete your subscription.');
        } else {
          setSubscriptionMessage('Check your email and click the confirmation link to complete your subscription.');
        }
        
        setSubscriptionEmail('');
      } else {
        setSubscriptionMessage(data.message || 'Failed to subscribe. Please try again.');
      }
    } catch (err) {
      console.error('Subscription error:', err);
      setSubscriptionMessage('Failed to subscribe. Please try again.');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Check for subscription_confirmed callback (when user clicks confirmation link in email)
  useEffect(() => {
    const subscriptionConfirmed = searchParams.get('subscription_confirmed');
    if (subscriptionConfirmed === 'true') {
      setShowSubscriptionConfirmedBanner(true);
      // Clear the query param from URL for cleaner UX
      searchParams.delete('subscription_confirmed');
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
      // Auto-dismiss banner after 5 seconds
      const timer = setTimeout(() => setShowSubscriptionConfirmedBanner(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [searchParams, setSearchParams]);

  // Load status favicon script (run once on mount)
  useEffect(() => {
    // Remove all platform favicon links so nothing fights the status favicon
    const allFavicons = document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]');
    const removed = [];
    allFavicons.forEach(link => {
      removed.push({ parent: link.parentNode, ref: link.nextSibling, el: link });
      link.parentNode.removeChild(link);
    });

    // Create a single clean status favicon link
    const statusFavicon = document.createElement('link');
    statusFavicon.rel = 'icon';
    statusFavicon.type = 'image/x-icon';
    statusFavicon.href = '/icons/favicon_operational.ico';
    document.head.appendChild(statusFavicon);

    const script = document.createElement('script');
    script.src = '/status-favicon-switcher.js';
    script.async = true;
    
    script.onload = () => {
      console.log('StatusPage: Favicon script loaded');
      
      if (window.clearFaviconRetries) {
        window.clearFaviconRetries();
      }
    };
    
    script.onerror = () => {
      console.error('StatusPage: Failed to load favicon script');
    };
    
    document.head.appendChild(script);

    return () => {
      // Remove status favicon
      if (statusFavicon.parentNode) {
        statusFavicon.parentNode.removeChild(statusFavicon);
      }
      // Restore original platform favicon links
      removed.forEach(({ parent, ref, el }) => {
        if (parent) parent.insertBefore(el, ref);
      });
      // Remove the script element
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []); // Run only once on mount

  // Separate effect to update favicon when status changes
  useEffect(() => {
    if (currentStatus && window.updateStatusFavicon) {
      console.log('StatusPage: Status changed, updating favicon');
      updateFavicon(currentStatus);
    }
  }, [currentStatus, updateFavicon]);

  // Load all data
  useEffect(() => {
    const loadData = async (isInitialLoad = false) => {
      // Only show loading spinner on initial load
      if (isInitialLoad) {
        setLoading(true);
      }
      
      try {
        await Promise.all([
          fetchCurrentStatus(),
          fetchIncidents(),
          fetchUptime()
        ]);
        
        // Update last updated timestamp
        setLastUpdated(new Date());
      } catch (err) {
        console.error('Failed to load status information:', err);
      } finally {
        // Only hide loading spinner if this was the initial load
        if (isInitialLoad) {
          setLoading(false);
        }
      }
    };

    // Initial load with loading state
    loadData(true);

    // Background refresh every 30 seconds without loading state
    const interval = setInterval(() => loadData(false), 30000);
    return () => clearInterval(interval);
  }, [fetchCurrentStatus, fetchIncidents, fetchUptime]);



  // Generate daily status for parent services (like Midus) based on child components
  const generateParentServiceDailyStatus = (parentServiceName, childComponents) => {
    const barDates = getBarDates();
    const todayDate = new Date();

    return barDates.map((date) => {
      if (parentServiceName === 'Midus for Compliance' && dayIsBeforeComplianceLaunch(date)) {
        return { date, status: 'none', incident: null, prelaunch: true };
      }

      const isToday = date.toDateString() === todayDate.toDateString();

      let status = 'operational'; // green
      let incident = null;
      
      // Check if any child component is affected today by current status
      if (isToday && currentStatus && currentStatus.isActive) {
        const childServiceKeys = childComponents.map(comp => comp.serviceKey || comp.name.toLowerCase().replace(/\s+/g, ''));
        const affectedChild = childServiceKeys.find(key => {
          return currentStatus.affectedServices?.includes(key);
        });
        
        if (affectedChild || currentStatus.affectedServices?.includes('all')) {
          if (currentStatus.severity === 'critical') {
            status = 'major'; // red
          } else {
            status = 'minor'; // yellow (medium)
          }
          incident = {
            title: currentStatus.title || 'System Maintenance',
            message: currentStatus.message || 'System maintenance in progress',
            severity: currentStatus.severity || 'medium'
          };
        }
      }
      
      // If no current status affected this service, check historical incidents
      if (status === 'operational') {
        const dayIncidents = incidents.filter((inc) => incidentAffectsCalendarDay(inc, date));
        
        if (dayIncidents.length > 0) {
          const relevantIncident = dayIncidents.find(inc => {
            if (inc.affectedServices?.includes('all')) return true;
            const childServiceKeys = childComponents.map(comp => comp.serviceKey || comp.name.toLowerCase().replace(/\s+/g, ''));
            return childServiceKeys.some(key => inc.affectedServices?.includes(key));
          });
          
          if (relevantIncident) {
            if (relevantIncident.severity === 'critical') {
              status = 'major'; // red
            } else {
              status = 'minor'; // yellow (medium)
            }
            incident = relevantIncident;
          }
        }
      }
      
      return {
        date,
        status,
        incident
      };
    });
  };

  // Generate daily status data (60 days on mobile, 90 on desktop, or from trackingSince)
  const generateDailyStatus = (serviceName) => {
    const barDates = getBarDates();
    const todayDate = new Date();

    return barDates.map((date) => {
      if (complianceLaunchStart && isComplianceStatusService(serviceName) && dayIsBeforeComplianceLaunch(date)) {
        return { date, status: 'none', incident: null, prelaunch: true };
      }

      const isToday = date.toDateString() === todayDate.toDateString();

      let status = 'operational'; // green
      let incident = null;
      
      const dayIncidents = incidents.filter((inc) => incidentAffectsCalendarDay(inc, date));
      
      // Check if this specific service was affected
      const isServiceAffectedOnDay = (serviceNameToCheck, statusToCheck) => {
        if (!statusToCheck) return false;
        
        if (statusToCheck.affectedServices?.includes('all')) return true;
        
        const serviceMap = {
          'APIs': 'apis',
          'apis': 'apis',
          'Login': 'login',
          'login': 'login',
          'Signup': 'signup',
          'signup': 'signup',
          'Chat': 'chat',
          'chat': 'chat',
          'Search': 'search',
          'search': 'search',
          'Deep Research': 'deep_research',
          'deepresearch': 'deep_research',
          'Image Gen': 'image_gen',
          'imagegen': 'image_gen',
          'Connectors': 'connectors',
          'connectors': 'connectors',
          'Memory': 'memory',
          'memory': 'memory',
          'SuperBucket': 'superbucket',
          'superbucket': 'superbucket',
          'Projects': 'projects',
          'projects': 'projects',
          'heymidus.com': 'heymidus.com',
          'Midus for Compliance': 'compliance',
          'midus for compliance': 'compliance',
          'compliance': 'compliance',
          'compliance_login': 'compliance_login',
          'parsing': 'parsing',
          'downloads': 'downloads',
          'rulebooks': 'rulebooks'
        };
        
        const mappedServiceName = serviceMap[serviceNameToCheck];
        return mappedServiceName && statusToCheck.affectedServices?.includes(mappedServiceName);
      };

      const isAffectedByCurrentStatus = isServiceAffectedOnDay(serviceName, currentStatus);
      
      if (isToday && currentStatus && currentStatus.isActive && isAffectedByCurrentStatus) {
        if (currentStatus.severity === 'critical') {
          status = 'major'; // red
        } else {
          status = 'minor'; // yellow (medium)
        }
        incident = {
          title: currentStatus.title || 'System Maintenance',
          message: currentStatus.message || 'System maintenance in progress',
          severity: currentStatus.severity || 'medium'
        };
      } else if (dayIncidents.length > 0) {
        const relevantIncident = dayIncidents.find(inc => isServiceAffectedOnDay(serviceName, inc));
        
        if (relevantIncident) {
          if (relevantIncident.severity === 'critical') {
            status = 'major'; // red
          } else {
            status = 'minor'; // yellow (medium)
          }
          incident = relevantIncident;
        }
      }
      
      return {
        date,
        status,
        incident
      };
    });
  };

  const formatBarDayTitle = (day) => {
    if (day.incident) return `${day.date.toLocaleDateString()}: ${day.incident.message}`;
    if (day.prelaunch) return `${day.date.toLocaleDateString()}: Midus for Compliance not yet available`;
    if (day.status === 'none') return `${day.date.toLocaleDateString()}: No data`;
    return `${day.date.toLocaleDateString()}: Operational`;
  };

  // Get status color for daily bars
  const getStatusColor = (status) => {
    switch (status) {
      case 'major': return 'bg-red-500';
      case 'minor': return 'bg-yellow-500';
      case 'maintenance': return 'bg-blue-500';
      case 'none': return 'bg-[#d1d5db] dark:bg-[#282828]';
      default: return 'bg-green-500';
    }
  };

  const getDateRange = () => {
    const dates = getBarDates();
    if (dates.length === 0) {
      const today = new Date();
      return {
        startMonth: today.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        endMonth: today.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        totalDays: 0
      };
    }
    const startDate = dates[0];
    const endDate = dates[dates.length - 1];
    return {
      startMonth: startDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
      endMonth: endDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
      totalDays: dates.length
    };
  };

  // Toggle service expansion
  const toggleServiceExpansion = (serviceName) => {
    setExpandedServices(prev => ({
      ...prev,
      [serviceName]: !prev[serviceName]
    }));
  };

  // Check if a service is affected by current status
  const isServiceAffected = (serviceName) => {
    if (!currentStatus?.isActive) return false;
    if (currentStatus.affectedServices?.includes('all')) return true;
    
    // Map service names to the format used in affectedServices
    const serviceMap = {
      'APIs': 'apis',
      'Midus': 'midus',
      'Login': 'login',
      'Signup': 'signup', 
      'Chat': 'chat',
      'Search': 'search',
      'Deep Research': 'deep_research',
      'Image Gen': 'image_gen',
      'Connectors': 'connectors',
      'Memory': 'memory',
      'SuperBucket': 'superbucket',
      'Projects': 'projects',
      'heymidus.com': 'heymidus.com',
      'Midus for Compliance': 'compliance',
      'Downloads': 'downloads',
      'Parsing': 'parsing',
      'Rulebooks': 'rulebooks'
    };
    
    const mappedServiceName = serviceMap[serviceName];
    if (mappedServiceName && currentStatus.affectedServices?.includes(mappedServiceName)) {
      return true;
    }
    
    // For parent services like 'Midus', check if any child components are affected
    if (serviceName === 'Midus') {
      const childServices = ['login', 'signup', 'chat', 'search', 'deep_research', 'image_gen', 'connectors', 'memory', 'superbucket', 'projects', 'heymidus.com'];
      return childServices.some(child => currentStatus.affectedServices?.includes(child));
    }
    
    // For Midus for Compliance, check if any child components are affected
    if (serviceName === 'Midus for Compliance') {
      const childServices = ['compliance', 'compliance_login', 'parsing', 'downloads', 'rulebooks'];
      return childServices.some(child => currentStatus.affectedServices?.includes(child));
    }
    
    return false;
  };

  // Get status color for service indicator
  const getServiceStatusColor = (serviceName) => {
    if (!isServiceAffected(serviceName)) {
      return 'bg-green-500'; // Operational
    }
    
    // Return color based on severity
    if (currentStatus?.severity === 'critical') {
      return 'bg-red-500'; // Critical issue
    } else {
      return 'bg-yellow-500'; // Medium issue
    }
  };

  // Calculate service-specific uptime based on 90-day data
  const calculateServiceUptime = (serviceName) => {
    // Midus for Compliance services: use their own uptime, never inherit from Midus
    const complianceServices = ['compliance', 'compliance_login', 'parsing', 'downloads', 'rulebooks'];
    if (complianceServices.includes(serviceName)) {
      if (uptime?.services?.[serviceName]) {
        return `${uptime.services[serviceName].percentage}%`;
      }
      return '100%'; // Day 1 - no historical data yet
    }
    
    if (!uptime || !uptime.services) return '99.9%';
    
    const serviceData = uptime.services[serviceName];
    if (serviceData) {
      return `${serviceData.percentage}%`;
    }
    
    return `${uptime.last90Days.percentage}%`;
  };

  // Define service structure
  const services = [
    {
      name: 'APIs',
      uptime: calculateServiceUptime('apis'),
      expandable: false
    },
    {
      name: 'Midus',
      uptime: calculateServiceUptime('midus'),
      expandable: true,
      components: [
        { name: 'Login', uptime: calculateServiceUptime('login') },
        { name: 'Signup', uptime: calculateServiceUptime('signup') },
        { name: 'Chat', uptime: calculateServiceUptime('chat') },
        { name: 'Search', uptime: calculateServiceUptime('search') },
        { name: 'Deep Research', uptime: calculateServiceUptime('deep_research') },
        { name: 'Image Gen', uptime: calculateServiceUptime('image_gen') },
        { name: 'Connectors', uptime: calculateServiceUptime('connectors') },
        { name: 'Memory', uptime: calculateServiceUptime('memory') },
        { name: 'SuperBucket', uptime: calculateServiceUptime('superbucket') },
        { name: 'Projects', uptime: calculateServiceUptime('projects') },
        { name: 'heymidus.com', uptime: calculateServiceUptime('heymidus.com') }
      ]
    },
    {
      name: 'Midus for Compliance',
      uptime: calculateServiceUptime('compliance'),
      expandable: true,
      components: [
        { name: 'Login', serviceKey: 'compliance_login', uptime: calculateServiceUptime('compliance_login') },
        { name: 'Parsing', serviceKey: 'parsing', uptime: calculateServiceUptime('parsing') },
        { name: 'Downloads', serviceKey: 'downloads', uptime: calculateServiceUptime('downloads') },
        { name: 'Rulebooks', serviceKey: 'rulebooks', uptime: calculateServiceUptime('rulebooks') }
      ]
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white dark:bg-[#111111]">
        <NvgtrShimmerLoader darkMode={darkMode} size={60} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-[#111111] flex flex-col">
      {/* Header */}
      <div className="border-b border-[#d1d5db] dark:border-[#282828]">
        <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <a href="https://heymidus.com" className="hover:opacity-80 transition-opacity">
                <img 
                  src={darkMode ? midusStatusWhite : midusStatusBlack}
                  alt="Midus" 
                  className="h-5"
                />
              </a>
            </div>
            <button 
              onClick={() => setShowSubscriptionModal(true)}
              className="text-xs text-[#6b7280] dark:text-[#9ca3af] hover:text-[#181818] dark:hover:text-white transition-colors"
            >
              Subscribe to updates
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 w-full max-w-4xl mx-auto px-4 sm:px-6 py-6">
        {activeTab === 'status' ? (
          <>
        {/* Subscription Confirmed Banner - above status bar */}
        {showSubscriptionConfirmedBanner && (
          <div className="status-squircle bg-green-50 border border-green-200 dark:bg-green-950/20 dark:border-green-800/30 p-4 mb-6">
            <div className="flex items-center space-x-3">
              <CheckIcon className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" strokeWidth={2.5} />
              <p className="font-medium text-[#181818] dark:text-white">
                You have subscribed to status updates.
              </p>
            </div>
          </div>
        )}

        {/* Current Status Banner */}
        {currentStatus?.isActive ? (
          <div className={`status-squircle border p-4 mb-6 ${
            currentStatus.severity === 'critical'
              ? 'bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800/30' 
              : 'bg-yellow-50 border-yellow-200 dark:bg-yellow-950/20 dark:border-yellow-800/30'
          }`}>
            <div className="flex items-start space-x-3">
              <div className={`w-5 h-5 rounded-full mt-0.5 ${
                currentStatus.severity === 'critical'
                  ? 'bg-red-500' 
                  : 'bg-yellow-500'
              }`}></div>
              <div className="flex-1">
                <h3 className="font-semibold text-[#181818] dark:text-white mb-1">
                  {currentStatus.title}
                </h3>
                <p className="text-[#6b7280] dark:text-[#9ca3af] mb-3">
                  {currentStatus.message}
                </p>
                {currentStatus.estimatedDuration && (
                  <p className="text-sm text-[#6b7280] dark:text-[#9ca3af]">
                    <strong>Estimated Duration:</strong> {currentStatus.estimatedDuration}
                  </p>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="status-squircle bg-green-50 border border-green-200 dark:bg-green-950/20 dark:border-green-800/30 p-4 mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-4 h-4 bg-green-500 rounded-full"></div>
              <div>
                <h3 className="font-medium text-[#181818] dark:text-white">
                  We're fully operational
                </h3>
                <p className="text-sm text-[#6b7280] dark:text-[#9ca3af] mt-1">
                  We're not aware of any issues affecting our systems.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* System Status */}
        <div className="status-squircle bg-white dark:bg-[#181818] border border-[#d1d5db] dark:border-[#282828] p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-[#181818] dark:text-white">
              System status
            </h2>
            <div className="text-xs text-[#6b7280] dark:text-[#9ca3af]">
              {getDateRange().startMonth} - {getDateRange().endMonth}
            </div>
          </div>

          {/* Service Components */}
          <div className="space-y-4">
            {services.map((service) => (
              <div key={service.name}>
                {service.expandable && expandedServices[service.name] ? (
                  // Expanded View - Show only header and components
                  <div className="space-y-2">
                    {/* Service Header Only */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-[#181818] dark:text-white">{service.name}</span>
                        <button
                          onClick={() => toggleServiceExpansion(service.name)}
                          className="p-1 text-[#6b7280] dark:text-[#9ca3af] hover:text-[#181818] dark:hover:text-white transition-colors rounded"
                        >
                          <svg 
                            className="w-3 h-3 transform transition-transform rotate-180"
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        <span className="text-xs text-[#6b7280] dark:text-[#9ca3af]">
                          {service.components.length} components
                        </span>
                      </div>
                    </div>

                    {/* Individual Components - Full Size */}
                    {service.components.map((component) => (
                      <div key={component.name} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <div className={`w-3 h-3 ${getServiceStatusColor(component.name)} rounded-full`}></div>
                            <span className="text-sm font-medium text-[#181818] dark:text-white">{component.name}</span>
                          </div>
                          <div className="text-xs text-[#6b7280] dark:text-[#9ca3af]">
                            {component.uptime} uptime
                          </div>
                        </div>
                        <div className="flex gap-0.5 md:gap-1 w-full min-h-[20px] md:min-h-[24px]">
                          {generateDailyStatus(component.serviceKey || component.name.toLowerCase().replace(/\s+/g, '')).map((day, index) => (
                            <div
                              key={index}
                              className={`flex-1 min-w-[4px] min-h-[16px] h-4 sm:h-5 md:h-6 ${getStatusColor(day.status)} rounded-sm relative group cursor-pointer`}
                              title={formatBarDayTitle(day)}
                            >
                                                             <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-[#181818] dark:bg-white text-white dark:text-[#181818] text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity z-30 pointer-events-none" style={{width: 'max-content', maxWidth: '200px'}}>
                                {day.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                {day.prelaunch && !day.incident && (
                                  <div className="text-xs mt-1 opacity-90">Not yet available</div>
                                )}
                                {day.incident && (
                                  <div className="text-xs mt-1">{day.incident.message}</div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  // Collapsed View - Show main service with status bars
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className={`w-3 h-3 ${getServiceStatusColor(service.name)} rounded-full`}></div>
                        <span className="text-sm font-medium text-[#181818] dark:text-white">{service.name}</span>
                        {service.expandable && (
                          <button
                            onClick={() => toggleServiceExpansion(service.name)}
                            className="p-1 text-[#6b7280] dark:text-[#9ca3af] hover:text-[#181818] dark:hover:text-white transition-colors rounded"
                          >
                            <svg 
                              className="w-3 h-3 transform transition-transform"
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                        )}
                        {service.expandable && (
                          <span className="text-xs text-[#6b7280] dark:text-[#9ca3af]">
                            {service.components.length} components
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-[#6b7280] dark:text-[#9ca3af]">
                        {service.uptime} uptime
                      </div>
                    </div>
                    <div className="flex gap-0.5 md:gap-1 w-full min-h-[20px] md:min-h-[24px]">
                      {(service.expandable
                        ? generateParentServiceDailyStatus(service.name, service.components)
                        : generateDailyStatus(service.name.toLowerCase())
                      ).map((day, index) => (
                        <div
                          key={index}
                          className={`flex-1 min-w-[4px] min-h-[16px] h-4 sm:h-5 md:h-6 ${getStatusColor(day.status)} rounded-sm relative group cursor-pointer`}
                          title={formatBarDayTitle(day)}
                        >
                                                     <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-[#181818] dark:bg-white text-white dark:text-[#181818] text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity z-20 pointer-events-none" style={{width: 'max-content', maxWidth: '300px'}}>
                            {day.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            {day.prelaunch && !day.incident && (
                              <div className="text-xs mt-1 opacity-90">Not yet available</div>
                            )}
                            {day.incident && (
                              <div className="text-xs mt-1">{day.incident.message}</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

        </div>

        {/* View History Button - Outside Container */}
        <div className="flex justify-center mt-6">
          <button 
            onClick={() => setActiveTab('history')}
            className="status-squircle-sm inline-flex items-center px-3 py-2 text-xs font-medium text-[#6b7280] dark:text-[#9ca3af] bg-white dark:bg-[#181818] border border-[#d1d5db] dark:border-[#282828] hover:bg-[#f2f2f2] dark:hover:bg-[#282828] transition-colors"
          >
            <svg className="w-3 h-3 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            View history
          </button>
        </div>
          </>
        ) : (
          // History Tab
          <div>
            {/* Breadcrumb Navigation */}
            <div className="mb-8">
              <div className="flex items-center text-sm text-[#6b7280] dark:text-[#9ca3af]">
                <button 
                  onClick={() => setActiveTab('status')}
                  className="hover:text-[#181818] dark:hover:text-white transition-colors"
                >
                  Midus
                </button>
                <span className="mx-2">/</span>
                <span className="text-[#181818] dark:text-white">History</span>
              </div>
            </div>

            {/* History Header */}
            <div className="flex items-center justify-between mb-8">
              <h1 className="text-2xl font-semibold text-[#181818] dark:text-white">
                History
              </h1>
              <div className="flex items-center text-sm text-[#6b7280] dark:text-[#9ca3af]">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span>{getDateRange().startMonth} - {getDateRange().endMonth}</span>
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>

        {/* History Section */}
        <div className="space-y-8">
          {/* Month Header */}
          <div>
            <h2 className="text-lg font-medium text-[#181818] dark:text-white mb-6">
              August
            </h2>
            
            {/* Incident Timeline */}
            {incidents.length > 0 ? (
              <div className="space-y-6">
                {incidents.map((incident) => (
                  <div key={incident.id} className="flex items-start">
                    {/* Date Column */}
                    <div className="flex flex-col items-center w-16 mr-6">
                      <div className="text-lg font-medium text-[#181818] dark:text-white">
                        {new Date(incident.resolvedAt).getDate()}
                      </div>
                      <div className="text-xs text-[#6b7280] dark:text-[#9ca3af]">
                        {new Date(incident.resolvedAt).toLocaleDateString('en-US', { weekday: 'short' })}
                      </div>
                    </div>
                    
                    {/* Incident Content */}
                    <div className="flex-1">
                      <div className={`border-l-4 pl-4 ${
                        incident.severity === 'critical'
                          ? 'border-red-400'
                          : 'border-yellow-400'
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-medium text-[#181818] dark:text-white">
                            {incident.title}
                          </h3>
                          <div className="text-xs text-[#6b7280] dark:text-[#9ca3af]">
                            {new Date(incident.resolvedAt).toLocaleTimeString('en-US', { 
                              hour: 'numeric', 
                              minute: '2-digit',
                              hour12: true 
                            })}
                          </div>
                        </div>
                        <p className="text-sm text-[#6b7280] dark:text-[#9ca3af]">
                          All impacted services have now fully recovered.
                        </p>
                      </div>
                    </div>
                    
                    
                  </div>
                ))}
              </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-[#6b7280] dark:text-[#9ca3af] text-lg mb-2">
                No incidents to report
              </div>
              <p className="text-sm text-[#6b7280] dark:text-[#9ca3af]">
                We haven't had any reported incidents in the past 90 days.
              </p>
            </div>
            )}
          </div>
          </div>
        </div>
        )}
      </div>

      {/* Footer - Pinned to Bottom */}
      <div className="border-t border-[#d1d5db] dark:border-[#282828] mt-auto">
        <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 py-6 text-center">
          <p className="text-sm text-[#6b7280] dark:text-[#9ca3af] mb-2">
            Questions about our status? Contact us at{' '}
            <a 
              href="mailto:support@heymidus.com" 
              className="text-[#FFBDA3] hover:text-[#FFBDA3] dark:text-[#FFBDA3] dark:hover:text-[#FFBDA3]"
            >
              support@heymidus.com
            </a>
          </p>
          {lastUpdated && (
            <p className="text-xs text-[#9ca3af] dark:text-[#6b7280]">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </p>
          )}
        </div>
      </div>

      {/* Subscription Modal */}
      {showSubscriptionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="status-squircle-lg bg-white dark:bg-[#181818] border border-[#d1d5db] dark:border-[#282828] p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#181818] dark:text-white">
                Subscribe to Updates
              </h3>
              <button
                onClick={() => {
                  setShowSubscriptionModal(false);
                  setSubscriptionMessage('');
                  setSubscriptionEmail('');
                  setSubscriptionServices(['apis', 'midus', 'compliance']);
                }}
                className="text-[#6b7280] dark:text-[#9ca3af] hover:text-[#181818] dark:hover:text-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <p className="text-sm text-[#6b7280] dark:text-[#9ca3af] mb-4">
              Get notified via email when we post status updates about incidents, maintenance, and service recovery.
            </p>
            
            <form onSubmit={handleSubscription}>
              <div className="mb-4">
                <label htmlFor="email" className="block text-sm font-medium text-[#181818] dark:text-white mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  value={subscriptionEmail}
                  onChange={(e) => setSubscriptionEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="status-squircle-sm w-full px-3 py-2 border border-[#d1d5db] dark:border-[#282828] bg-white dark:bg-[#111111] text-[#181818] dark:text-white placeholder-[#6b7280] dark:placeholder-[#9ca3af] focus:outline-none focus:border-[#9ca3af] dark:focus:border-[#6b7280]"
                  required
                  disabled={subscriptionLoading}
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-[#181818] dark:text-white mb-2">
                  Services
                </label>
                <div className="space-y-2">
                  {[
                    { key: 'apis', label: 'APIs' },
                    { key: 'midus', label: 'Midus' },
                    { key: 'compliance', label: 'Midus for Compliance' }
                  ].map(({ key, label }) => {
                    const isChecked = subscriptionServices.includes(key);
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => {
                          setSubscriptionServices(prev =>
                            prev.includes(key)
                              ? prev.filter(s => s !== key)
                              : [...prev, key]
                          );
                        }}
                        disabled={subscriptionLoading}
                        className={`status-squircle-sm w-full flex items-center px-3 py-2.5 border transition-colors text-left ${
                          isChecked
                            ? 'border-[#9ca3af] dark:border-[#6b7280] bg-white dark:bg-[#111111]'
                            : 'border-[#d1d5db] dark:border-[#282828] bg-white dark:bg-[#111111]'
                        } hover:border-[#9ca3af] dark:hover:border-[#6b7280] disabled:opacity-50`}
                      >
                        <div className={`w-4 h-4 rounded flex-shrink-0 flex items-center justify-center border transition-colors ${
                          isChecked
                            ? 'bg-[#181818] dark:bg-white border-[#181818] dark:border-white'
                            : 'border-[#d1d5db] dark:border-[#282828] bg-transparent'
                        }`}>
                          {isChecked && (
                            <svg className="w-2.5 h-2.5 text-white dark:text-[#181818]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <span className="ml-3 text-sm text-[#181818] dark:text-white">{label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {subscriptionMessage && (
                <div className={`status-squircle-sm mb-4 p-3 text-sm flex items-center space-x-2 ${
                  subscriptionMessage.includes('Successfully') || subscriptionMessage.includes('resubscribed')
                    ? 'bg-green-50 border border-green-200 text-green-800 dark:bg-green-950/20 dark:border-green-800/30 dark:text-green-400'
                    : subscriptionMessage.includes('already subscribed')
                    ? 'bg-blue-50 border border-blue-200 text-blue-800 dark:bg-blue-950/20 dark:border-blue-800/30 dark:text-blue-400'
                    : subscriptionMessage.includes('check your email') || subscriptionMessage.includes('confirmation')
                    ? 'bg-blue-50 border border-blue-200 text-blue-800 dark:bg-blue-950/20 dark:border-blue-800/30 dark:text-blue-400'
                    : 'bg-red-50 border border-red-200 text-red-800 dark:bg-red-950/20 dark:border-red-800/30 dark:text-red-400'
                }`}>
                  {/* Icon based on message type */}
                  {(subscriptionMessage.includes('Successfully') || subscriptionMessage.includes('resubscribed')) && (
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  {(subscriptionMessage.includes('already subscribed') || subscriptionMessage.includes('check your email')) && (
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                  {(!subscriptionMessage.includes('Successfully') && !subscriptionMessage.includes('resubscribed') && !subscriptionMessage.includes('already subscribed') && !subscriptionMessage.includes('check your email')) && (
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                  <span>{subscriptionMessage}</span>
                </div>
              )}
              
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowSubscriptionModal(false);
                    setSubscriptionMessage('');
                    setSubscriptionEmail('');
                    setSubscriptionServices(['apis', 'midus', 'compliance']);
                  }}
                  className="status-squircle-sm flex-1 px-4 py-2 text-sm font-medium text-[#6b7280] dark:text-[#9ca3af] bg-white dark:bg-[#111111] border border-[#d1d5db] dark:border-[#282828] hover:bg-[#f9fafb] dark:hover:bg-[#282828] transition-colors"
                  disabled={subscriptionLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="status-squircle-sm flex-1 px-4 py-2 text-sm font-medium text-white dark:text-black dark:bg-white bg-black hover:bg-black/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  disabled={subscriptionLoading || subscriptionServices.length === 0}
                >
                  {subscriptionLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Subscribing...
                    </>
                  ) : (
                    'Subscribe'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatusPage;
